import { useEffect, useMemo, useState } from 'react';

import { useProductFamilies } from '../hooks';
import type { AttributeCompleteness, ProductFamily } from '../model/types';

export interface ProductFamilyResolutionHints {
  /** `ProductDetail.attributeCompleteness` — the backend computes it against the ASSIGNED family. */
  attributeCompleteness?: AttributeCompleteness | null;
  /** Codes of the product's typed `attributeValues` — the inference tie-breaker. */
  attributeValueCodes?: readonly string[];
}

export interface ResolvedProductFamily {
  families: ProductFamily[];
  familiesLoading: boolean;
  assignedFamily: ProductFamily | null;
  /** True when `familyId` is set but no family `code` could be resolved for it — see below. */
  unresolvable: boolean;
  /** Call with the `code` just assigned (or `null` on unassign) so this stays resolved without a refetch. */
  markAssignedCode: (code: string | null) => void;
}

/**
 * Fingerprint inference (adr-merchant-portal-008): identify the assigned family from
 * `attributeCompleteness`, which the backend computes against the family the `familyId` UUID
 * points to. A family is a candidate only when the completeness snapshot is EXACTLY what that
 * family would produce for this product:
 *
 *   1. its required-attribute count equals `completeness.required`,
 *   2. every `completeness.missing` code IS one of its required attributes, and
 *   3. every other required attribute (the "filled" ones) actually has a typed value on the
 *      product.
 *
 * A single candidate is a resolution. Several candidates fall to a coverage tie-break (the family
 * whose attribute list explains the most of the product's typed values); a residual tie stays
 * UNRESOLVED — showing no template is safer than showing a wrong one that the merchant might then
 * "confirm" into a real reassignment.
 */
function inferFamilyFromCompleteness(
  families: ProductFamily[],
  completeness: AttributeCompleteness | null | undefined,
  valueCodes: readonly string[],
): ProductFamily | null {
  if (!completeness) return null;
  const valueSet = new Set(valueCodes);
  const missingSet = new Set(completeness.missing);

  const candidates = families.filter((family) => {
    const requiredCodes = family.attributes.filter((a) => a.required).map((a) => a.attributeCode);
    if (requiredCodes.length !== completeness.required) return false;
    if (!completeness.missing.every((code) => requiredCodes.includes(code))) return false;
    return requiredCodes.every((code) => missingSet.has(code) || valueSet.has(code));
  });

  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) {
    const coverage = (family: ProductFamily): number =>
      family.attributes.filter((a) => valueSet.has(a.attributeCode)).length;
    const ranked = [...candidates].sort((a, b) => coverage(b) - coverage(a));
    if (coverage(ranked[0]) > coverage(ranked[1])) return ranked[0];
  }
  return null;
}

/**
 * Resolves `ProductDetail.familyId` to the assigned `ProductFamily` (for its name/attributes/axis
 * list) — shared by `ProductFamilyPanel` (the selector) and `ProductEditPage` (which needs the
 * same family to drive `ProductAttributesPanel`/`ProductVariantsSection`), so both stay in sync
 * off one resolution instead of two independent guesses.
 *
 * **Why this needs guessing at all:** `ProductDetail.familyId` is the family entity's UUID
 * (web-catalog-master-service stores `family.getId()` on assignment), while `PUT/DELETE
 * .../family` assigns by the family's stable `code` and `GET /product-families` returns families
 * keyed by `code` only (no `id` field, no `GET`-by-`familyId` lookup) — there is no endpoint in
 * the contract that turns a `familyId` back into a `code`. Until the contract exposes the family
 * `code` on the product read (flagged in user-story-merchant-portal-030's Notes), this resolves
 * it via, in order:
 *
 *   1. a `code` assigned earlier *in this session* (fully authoritative — this frontend made that
 *      exact call);
 *   2. a direct `code === familyId` match (exact, then case-insensitive — holds whenever the two
 *      coincide);
 *   3. the `attributeCompleteness` fingerprint inference (see `inferFamilyFromCompleteness`) —
 *      this is what makes the assigned template show up when an existing product's card is
 *      OPENED, not only after an assignment made in this same session.
 *
 * If none matches, `assignedFamily` is `null` and `unresolvable` is `true` — callers should not
 * guess further.
 */
export function useResolvedProductFamily(
  productId: string,
  familyId: string | null | undefined,
  hints?: ProductFamilyResolutionHints,
): ResolvedProductFamily {
  const familiesQuery = useProductFamilies();
  const [lastAssignedCode, setLastAssignedCode] = useState<string | null>(null);

  // A newly-loaded product starts with no session-local assignment memory of its own.
  useEffect(() => {
    setLastAssignedCode(null);
  }, [productId]);

  const families = useMemo(() => familiesQuery.data ?? [], [familiesQuery.data]);

  const completeness = hints?.attributeCompleteness ?? null;
  // Stabilised through a join key: the caller typically maps a fresh array per render.
  const valueCodesKey = (hints?.attributeValueCodes ?? []).join(',');
  const valueCodes = useMemo(
    () => (valueCodesKey === '' ? [] : valueCodesKey.split(',')),
    [valueCodesKey],
  );

  const assignedFamily = useMemo(() => {
    if (!familyId) return null;
    if (lastAssignedCode) {
      const bySession = families.find((f) => f.code === lastAssignedCode);
      if (bySession) return bySession;
    }
    const byCode =
      families.find((f) => f.code === familyId) ??
      families.find((f) => f.code.toLowerCase() === familyId.toLowerCase());
    if (byCode) return byCode;
    return inferFamilyFromCompleteness(families, completeness, valueCodes);
  }, [familyId, families, lastAssignedCode, completeness, valueCodes]);

  return {
    families,
    familiesLoading: familiesQuery.isLoading,
    assignedFamily,
    unresolvable: Boolean(familyId) && !assignedFamily && !familiesQuery.isLoading,
    markAssignedCode: setLastAssignedCode,
  };
}
